import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { TicketPriority, Department, type CreateTicketBody } from '@nbh/shared';
import { api } from '../../lib/api';
import { enqueueWrite } from '../../lib/db';
import { uploadAttachment, uploadFailureKey } from '../../lib/image';
import { PhotoPicker, type PendingPhoto } from './PhotoPicker';
import { ReportFields, type ReportDraft } from './ReportFields';
import { AssigneeField } from './AssigneeField';

export interface CreateTicketFormProps {
  locationId: string;
  locationName: string;
  onCreated?: (ticketId: string) => void;
  /** Back to the place picker — the location was chosen a screen ago. */
  onBack?: () => void;
  /** Abandon the report entirely. */
  onCancel?: () => void;
}

const EMPTY_DRAFT: ReportDraft = {
  title: '',
  description: '',
  priority: TicketPriority.Medium,
  department: Department.Maintenance,
};

/** Where the report ended up — created on the server, or queued for when signal returns. */
interface FiledReport {
  ticketId?: string;
  queued: boolean;
}

/** What became of the photos, which is a separate question from where the report went. */
interface PhotoOutcome {
  ids: string[];
  /** One message key per photo that never made it up, in capture order. */
  failures: string[];
}

/**
 * The report that is filed, and the photos that did not attach to it, held together
 * until the reporter has read the second half.
 */
interface LostPhotos {
  filed: FiledReport;
  failures: string[];
  total: number;
}

/**
 * Fault reporting (SPEC.md §5.1) — the entry point for the whole system.
 *
 * Laid out for one-handed use with gloves on: full-width controls and the submit
 * button last, in the bottom third where a thumb reaches.
 */
export function CreateTicketForm({
  locationId,
  locationName,
  onCreated,
  onBack,
  onCancel,
}: CreateTicketFormProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ReportDraft>(EMPTY_DRAFT);
  /** Set only by a dispatcher (D34); undefined means the department pool. */
  const [assigneeId, setAssigneeId] = useState<string | undefined>();
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  /**
   * Set only after the report is safely filed, and only when photos were lost getting
   * there. While it holds, the previews stay on screen and their blobs stay alive.
   */
  const [lostPhotos, setLostPhotos] = useState<LostPhotos | null>(null);

  /**
   * Is there anything here worth losing?
   *
   * Priority and department are excluded on purpose: they arrive pre-filled, so treating
   * them as evidence would prompt on the way out of a form nobody typed into, and a
   * confirm that always fires is one people learn to dismiss without reading.
   */
  function isDirty(): boolean {
    return (
      draft.title.trim() !== '' ||
      draft.description.trim() !== '' ||
      photos.length > 0 ||
      assigneeId !== undefined
    );
  }

  /** Runs `leave` unless there is unsaved work the reporter has not agreed to lose. */
  function confirmLeave(leave: () => void) {
    if (isDirty() && !window.confirm(t('report.discardConfirm'))) return;
    clearDraft();
    leave();
  }

  function clearDraft() {
    for (const photo of photos) URL.revokeObjectURL(photo.previewUrl);
    setPhotos([]);
    setDraft(EMPTY_DRAFT);
    setAssigneeId(undefined);
  }

  function removePhoto(id: string) {
    setPhotos((current) => {
      const removed = current.find((photo) => photo.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return current.filter((photo) => photo.id !== id);
    });
  }

  /** Hands the form back and frees the previews. The only place blobs are revoked. */
  function settle(filed: FiledReport) {
    clearDraft();
    if (filed.ticketId) onCreated?.(filed.ticketId);
  }

  /** The reporter has read what happened to their photos; now the previews may go. */
  function acknowledgeLostPhotos() {
    if (!lostPhotos) return;
    const { filed } = lostPhotos;
    setLostPhotos(null);
    settle(filed);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!draft.title.trim() || busy) return;

    setBusy(true);
    setError(null);
    setQueued(false);
    setLostPhotos(null);

    const photoOutcome = await uploadPhotos(photos);

    try {
      const filed = await fileReport(
        buildBody({ draft, locationId, assigneeId, attachmentIds: photoOutcome.ids }),
      );
      setQueued(filed.queued);

      // The report is filed by the time this line runs, online or queued, and nothing
      // below can unfile it — what is left to say is only about the photos. When some
      // were lost the previews and their blobs stay put until the reporter has read
      // that they did not attach; `settle` revokes them, and revoking them while the
      // message is still on screen would destroy the evidence behind their back.
      if (photoOutcome.failures.length > 0) {
        setLostPhotos({ filed, failures: photoOutcome.failures, total: photos.length });
      } else {
        settle(filed);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('report.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="flex flex-1 flex-col gap-4 p-4" onSubmit={handleSubmit}>
      {onBack && <BackToPicker onClick={() => confirmLeave(onBack)} />}

      <div>
        <h1 className="text-lg font-semibold">{t('report.heading')}</h1>
        <p className="text-sm text-slate-400">{locationName}</p>
      </div>

      <ReportFields
        draft={draft}
        onChange={(patch) => {
          setDraft({ ...draft, ...patch });
          // Moving the report to another department drops any assignee already chosen.
          // They are not a candidate there, and keeping them silently would dispatch
          // one team's technician onto another team's job.
          if (patch.department && patch.department !== draft.department) setAssigneeId(undefined);
        }}
      />

      <AssigneeField department={draft.department} value={assigneeId} onChange={setAssigneeId} />

      <PhotoPicker
        photos={photos}
        onAdd={(photo) => setPhotos((current) => [...current, photo])}
        onRemove={removePhoto}
        onError={setError}
      />

      <ReportStatus
        queued={queued}
        lostPhotos={lostPhotos}
        error={error}
        onAcknowledge={acknowledgeLostPhotos}
      />

      <ReportActions
        busy={busy}
        // Sealed while the photo notice is up. The draft is still on screen at that
        // point — deliberately, so nothing is destroyed unread — but the report it
        // describes has already been filed, and a second press would file it twice.
        canSubmit={draft.title.trim() !== '' && !busy && lostPhotos === null}
        onCancel={onCancel && (() => confirmLeave(onCancel))}
      />
    </form>
  );
}

/**
 * Sends the photos ahead of the report, keeping both halves of the answer.
 *
 * Best-effort by design: with no signal these fail and the report still goes out without
 * them — words now beat pictures later. What changed is that the failures are counted and
 * named instead of being dropped on the floor, because a photo that silently disappears
 * is indistinguishable to the reporter from one that arrived.
 */
async function uploadPhotos(photos: PendingPhoto[]): Promise<PhotoOutcome> {
  const results = await Promise.allSettled(
    photos.map((photo, index) => uploadAttachment(photo.blob, `report-${index}.webp`)),
  );

  return {
    ids: results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : [])),
    failures: results.flatMap((result) =>
      result.status === 'rejected' ? [uploadFailureKey(result.reason)] : [],
    ),
  };
}

function buildBody(input: {
  draft: ReportDraft;
  locationId: string;
  assigneeId?: string | undefined;
  attachmentIds: string[];
}): CreateTicketBody {
  return {
    ...input.draft,
    title: input.draft.title.trim(),
    description: input.draft.description.trim() || undefined,
    locationId: input.locationId,
    assigneeId: input.assigneeId,
    attachmentIds: input.attachmentIds.length > 0 ? input.attachmentIds : undefined,
    // Stamped now so the SLA runs from when the fault was seen, not when it synced.
    reportedAt: new Date(),
  };
}

/**
 * Gets the report out, one way or the other. Throws only when the server refused it.
 */
async function fileReport(body: CreateTicketBody): Promise<FiledReport> {
  try {
    const created = await api.createTicket(body);
    return { ticketId: created.id, queued: false };
  } catch (caught) {
    // fetch throws TypeError when the network itself failed. A server rejection
    // (400/401) must NOT queue: an invalid report stays invalid forever, and
    // retrying it would jam the outbox. Only unreachable counts as offline.
    const unreachable = !navigator.onLine || caught instanceof TypeError;
    if (!unreachable) throw caught;

    // Rule 4: a report is never lost to a dead plant-room signal. It queues with
    // its original reportedAt and syncs when the connection returns.
    await enqueueWrite({ method: 'POST', path: '/tickets', body });
    return { queued: true };
  }
}

/**
 * Everything the form has to say once submit has been pressed: that the report is
 * queued, that photos were lost on the way, that the server refused it. Distinct
 * outcomes and each can stand alone — a queued report whose photos also failed is one
 * situation described by two sentences, not a contradiction.
 */
function ReportStatus({
  queued,
  lostPhotos,
  error,
  onAcknowledge,
}: {
  queued: boolean;
  lostPhotos: LostPhotos | null;
  error: string | null;
  onAcknowledge: () => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      {queued && (
        <p role="status" className="text-sm text-amber-300">
          {t('report.queued')}
        </p>
      )}

      {lostPhotos && (
        <LostPhotosNotice
          failures={lostPhotos.failures}
          total={lostPhotos.total}
          onAcknowledge={onAcknowledge}
        />
      )}

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}
    </>
  );
}

/**
 * What happened to the photos, once the report itself is already safe.
 *
 * Not phrased as a failure of the report, because it is not one — the job exists and the
 * words are on it. It is an acknowledgement, and it is the reason the previews below are
 * still on screen: dismissing it is what finally revokes them.
 */
function LostPhotosNotice({
  failures,
  total,
  onAcknowledge,
}: {
  failures: string[];
  total: number;
  onAcknowledge: () => void;
}) {
  const { t } = useTranslation();
  // Ten photos lost to one dead link is one thing to say, not ten.
  const reasons = [...new Set(failures)];

  return (
    <div role="alert" className="rounded-xl border border-amber-500/50 bg-amber-950/40 p-3">
      <p className="text-sm font-semibold text-amber-200">
        {t('report.photosLost', { count: failures.length, total })}
      </p>
      {reasons.map((reason) => (
        <p key={reason} className="mt-1 text-sm text-amber-100/80">
          {t(reason)}
        </p>
      ))}
      <button
        type="button"
        onClick={onAcknowledge}
        className="mt-3 flex min-h-[56px] w-full items-center justify-center rounded-xl bg-amber-600 px-4 text-base font-semibold text-white"
      >
        {t('report.photosLostAck')}
      </button>
    </div>
  );
}

/** One step back to the place picker, not a page's worth of navigation. */
function BackToPicker({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      className="-ml-2 flex min-h-[44px] w-fit items-center gap-1 bg-transparent px-2 text-sm text-brookfields-gold-light"
    >
      <ChevronLeft className="size-4" aria-hidden="true" />
      {t('report.changeLocation')}
    </button>
  );
}

/**
 * Cancel and submit, pinned where a thumb already is.
 *
 * `mt-auto` puts this at the foot of the form when the content is short; `sticky` keeps
 * it on screen when the content is long. Offset by the tab bar's own height, because
 * `bottom-0` pins it *underneath* the bar — the same fault that made the job detail
 * actions render, sit inside the viewport, and refuse to be tapped.
 *
 * The negative margin lets the bar span the full width while the form keeps its padding,
 * so the backdrop reaches both edges instead of floating in a gutter.
 */
function ReportActions({
  busy,
  canSubmit,
  onCancel,
}: {
  busy: boolean;
  canSubmit: boolean;
  onCancel?: (() => void) | undefined;
}) {
  const { t } = useTranslation();

  return (
    <div className="sticky bottom-[var(--nav-h)] -mx-4 mt-auto flex gap-2 border-t border-slate-800 bg-slate-950/95 px-4 py-3 backdrop-blur">
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-[56px] flex-1 items-center justify-center rounded-xl bg-slate-800 text-base font-semibold text-slate-100"
        >
          {t('report.cancel')}
        </button>
      )}
      {/* Twice the width of Cancel: the two are not equally likely, and on a phone the
          bigger target should be the one people actually came here to press. */}
      <button
        type="submit"
        disabled={!canSubmit}
        className="flex min-h-[56px] flex-[2] items-center justify-center gap-2 rounded-xl bg-red-600 text-base font-semibold text-white disabled:opacity-40"
      >
        {busy && <Loader2 className="size-5 animate-spin" aria-hidden="true" />}
        {busy ? t('report.submitting') : t('report.submit')}
      </button>
    </div>
  );
}
